import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, imageUrl } from '../api/client.js';
import { useAuth } from '../auth/useAuth.js';

const STATUS_BADGE = {
  ready: 'bg-green-100 text-green-700',
  hidden: 'bg-gray-200 text-gray-700',
  draft: 'bg-blue-100 text-blue-700',
  generating: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
};

export default function AdminPage() {
  const { logout } = useAuth();
  const [tab, setTab] = useState('stories');

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-brand-700">Panel de administracion</h1>
          <p className="text-sm text-gray-600">Modera cuentos publicados y gestiona la blacklist.</p>
        </div>
        <button onClick={logout} className="text-sm text-brand-600 underline">Cerrar sesion</button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-brand-100">
        <TabButton active={tab === 'stories'} onClick={() => setTab('stories')}>Cuentos</TabButton>
        <TabButton active={tab === 'invites'} onClick={() => setTab('invites')}>Invitaciones</TabButton>
        <TabButton active={tab === 'blacklist'} onClick={() => setTab('blacklist')}>Blacklist</TabButton>
        <TabButton active={tab === 'transfer'} onClick={() => setTab('transfer')}>Exportar / Importar</TabButton>
        <TabButton active={tab === 'log'} onClick={() => setTab('log')}>Log moderacion</TabButton>
      </div>

      <div className="mt-6">
        {tab === 'stories' && <StoriesTab />}
        {tab === 'invites' && <InvitesTab />}
        {tab === 'blacklist' && <BlacklistTab />}
        {tab === 'transfer' && <TransferTab />}
        {tab === 'log' && <LogTab />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${
        active
          ? 'border-brand-600 text-brand-700'
          : 'border-transparent text-gray-500 hover:text-brand-600'
      }`}
    >
      {children}
    </button>
  );
}

// ============================================================
// Tab: Cuentos
// ============================================================
function StoriesTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setError(null);
    api.admin.listStories({ page: 1, pageSize: 100 }).then(setData).catch((e) => setError(e.message));
  }, []);
  useEffect(load, [load]);

  async function act(story, action) {
    if (action === 'delete' && !confirm(`Borrar definitivamente "${story.title}"?`)) return;
    setBusyId(story.id);
    try {
      await api.admin.storyAction(story.id, action);
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-gray-500">Cargando...</p>;
  if (data.items.length === 0) return <p className="text-gray-500">No hay cuentos todavia.</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-brand-100 bg-white">
      <table className="w-full text-left text-sm">
        <thead className="bg-brand-50 text-xs uppercase text-brand-700">
          <tr>
            <th className="px-3 py-2">Imagen</th>
            <th className="px-3 py-2">Titulo</th>
            <th className="px-3 py-2">Estado</th>
            <th className="px-3 py-2">Idioma</th>
            <th className="px-3 py-2">Estilo</th>
            <th className="px-3 py-2">Fecha</th>
            <th className="px-3 py-2">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((s) => (
            <tr key={s.id} className="border-t border-brand-50">
              <td className="px-3 py-2">
                {s.thumbnailImageId ? (
                  <img
                    src={imageUrl(s.thumbnailImageId)}
                    alt=""
                    className="h-12 w-12 rounded object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded bg-gray-100" />
                )}
              </td>
              <td className="px-3 py-2 font-semibold text-brand-700">
                <Link to={`/story/${s.slug}`} className="hover:underline">{s.title}</Link>
              </td>
              <td className="px-3 py-2">
                <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE[s.status] ?? ''}`}>
                  {s.status}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-gray-500">{s.language}</td>
              <td className="px-3 py-2 text-xs text-gray-500">{s.style}</td>
              <td className="px-3 py-2 text-xs text-gray-500">
                {new Date(s.created_at).toLocaleString()}
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-2">
                  {s.status === 'ready' && (
                    <button
                      disabled={busyId === s.id}
                      onClick={() => act(s, 'hide')}
                      className="rounded border border-amber-300 px-2 py-1 text-xs text-amber-700 hover:bg-amber-50"
                    >Ocultar</button>
                  )}
                  {s.status === 'hidden' && (
                    <button
                      disabled={busyId === s.id}
                      onClick={() => act(s, 'restore')}
                      className="rounded border border-green-300 px-2 py-1 text-xs text-green-700 hover:bg-green-50"
                    >Restaurar</button>
                  )}
                  <button
                    disabled={busyId === s.id}
                    onClick={() => act(s, 'delete')}
                    className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                  >Borrar</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Tab: Invitaciones (1 codigo = 1 cuento, sin acceso al admin)
// ============================================================
function InvitesTab() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [label, setLabel] = useState('');
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [justCreated, setJustCreated] = useState([]);

  const load = useCallback(() => {
    setError(null);
    api.admin.listInvites()
      .then((d) => setItems(d.items))
      .catch((e) => setError(e.message));
  }, []);
  useEffect(load, [load]);

  async function create(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { items: created } = await api.admin.createInvites({
        label: label.trim() || undefined,
        count: Math.max(1, Math.min(50, Number(count) || 1)),
      });
      setJustCreated(created.map((i) => i.code));
      setLabel('');
      setCount(1);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(code) {
    if (!confirm(`Borrar el codigo ${code}? Si ya esta en uso quien lo este usando no podra continuar.`)) return;
    setBusy(true);
    try {
      await api.admin.deleteInvite(code);
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  }

  function copyAll() {
    if (justCreated.length === 0) return;
    navigator.clipboard?.writeText(justCreated.join('\n'));
  }

  if (error && !items) return <p className="text-red-600">{error}</p>;
  if (!items) return <p className="text-gray-500">Cargando...</p>;

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-brand-100 bg-white p-4">
        <h3 className="font-semibold text-brand-700">Generar codigos</h3>
        <p className="mt-1 text-xs text-gray-500">
          Cada codigo permite crear <strong>un solo cuento</strong> sin necesitar la password
          general. Los codigos no dan acceso al panel de administracion. Una vez generado el cuento,
          el codigo se borra automaticamente.
        </p>
        <form className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_auto]" onSubmit={create}>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Nota opcional (ej: regalo para Juan)"
            maxLength={120}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            title="Cantidad"
          />
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            Generar
          </button>
        </form>
        {justCreated.length > 0 && (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-green-800">
                {justCreated.length === 1 ? 'Codigo creado' : `${justCreated.length} codigos creados`}
              </p>
              <button onClick={copyAll} className="text-xs text-green-700 underline">
                Copiar al portapapeles
              </button>
            </div>
            <ul className="mt-2 space-y-1 font-mono text-sm text-green-900">
              {justCreated.map((c) => (<li key={c}>{c}</li>))}
            </ul>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-brand-100 bg-white p-4">
        <h3 className="font-semibold text-brand-700">Codigos vigentes ({items.length})</h3>
        {items.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">No hay codigos generados.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-brand-50 text-xs uppercase text-brand-700">
                <tr>
                  <th className="px-3 py-2">Codigo</th>
                  <th className="px-3 py-2">Nota</th>
                  <th className="px-3 py-2">Creado</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.code} className="border-t border-brand-50">
                    <td className="px-3 py-2 font-mono text-xs">{it.code}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{it.label ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {new Date(it.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {it.used_story_id ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">
                          en uso
                        </span>
                      ) : (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-green-700">
                          disponible
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => remove(it.code)}
                        disabled={busy}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                      >
                        Borrar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================
// Tab: Blacklist
// ============================================================
function BlacklistTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [word, setWord] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    api.admin.blacklist().then(setData).catch((e) => setError(e.message));
  }, []);
  useEffect(load, [load]);

  async function add(e) {
    e.preventDefault();
    if (!word.trim()) return;
    setBusy(true);
    try {
      await api.admin.addBlacklist(word.trim());
      setWord('');
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(w) {
    if (!confirm(`Quitar "${w}" de la blacklist?`)) return;
    setBusy(true);
    try {
      await api.admin.removeBlacklist(w);
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-gray-500">Cargando...</p>;

  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <section className="rounded-xl border border-brand-100 bg-white p-4">
        <h3 className="font-semibold text-brand-700">Palabras dinamicas</h3>
        <p className="mt-1 text-xs text-gray-500">
          Se aplican en tiempo real al input de nuevos cuentos.
        </p>
        <form className="mt-3 flex gap-2" onSubmit={add}>
          <input
            value={word}
            onChange={(e) => setWord(e.target.value)}
            placeholder="palabra o frase"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={busy || !word.trim()}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >Añadir</button>
        </form>
        {data.dynamic.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500">(ninguna)</p>
        ) : (
          <ul className="mt-3 space-y-1">
            {data.dynamic.map((w) => (
              <li key={w} className="flex items-center justify-between rounded bg-brand-50 px-3 py-1.5 text-sm">
                <span className="font-mono">{w}</span>
                <button
                  onClick={() => remove(w)}
                  className="text-xs text-red-600 hover:underline"
                >quitar</button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-brand-100 bg-white p-4">
        <h3 className="font-semibold text-brand-700">Lista estatica (built-in)</h3>
        <p className="mt-1 text-xs text-gray-500">
          Codificada en el binario; modificarla requiere redeploy.
        </p>
        <div className="mt-3 flex flex-wrap gap-1">
          {data.static.map((w) => (
            <span key={w} className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs">{w}</span>
          ))}
        </div>
      </section>
    </div>
  );
}

// ============================================================
// Tab: Exportar / Importar cuentos entre entornos
// ============================================================
function TransferTab() {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <ExportSection />
      <ImportSection />
    </div>
  );
}

function ExportSection() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.admin.listStories({ page: 1, pageSize: 200 })
      .then((data) => setItems(data.items))
      .catch((e) => setError(e.message));
  }, []);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectAll() { setSelected(new Set(items.map((s) => s.id))); }
  function selectNone() { setSelected(new Set()); }

  async function doExport() {
    setBusy(true);
    setDone(null);
    setError(null);
    try {
      const ids = [...selected];
      const result = await api.admin.exportStories(ids);
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDone(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !items) return <section className="rounded-xl border border-red-200 bg-white p-4 text-red-600">{error}</section>;
  if (!items) return <section className="rounded-xl border border-brand-100 bg-white p-4 text-gray-500">Cargando cuentos...</section>;

  return (
    <section className="rounded-xl border border-brand-100 bg-white p-4">
      <h3 className="font-semibold text-brand-700">Exportar cuentos</h3>
      <p className="mt-1 text-xs text-gray-500">
        Genera un fichero <code>.db</code> con los cuentos seleccionados (texto, imagenes y audio).
        Util para llevarlos a otro entorno mediante la opcion de importar.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
        <span className="font-semibold text-gray-700">{selected.size} de {items.length} seleccionados</span>
        <button onClick={selectAll} className="text-brand-600 hover:underline">Todos</button>
        <button onClick={selectNone} className="text-gray-500 hover:underline">Ninguno</button>
      </div>

      <ul className="mt-3 max-h-80 space-y-1 overflow-y-auto rounded border border-brand-50 bg-brand-50/30 p-2">
        {items.map((s) => (
          <li key={s.id}>
            <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-white">
              <input
                type="checkbox"
                checked={selected.has(s.id)}
                onChange={() => toggle(s.id)}
              />
              <span className="flex-1 truncate text-sm text-gray-700">{s.title}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE[s.status] ?? ''}`}>{s.status}</span>
            </label>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={doExport}
          disabled={busy || selected.size === 0}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {busy ? 'Exportando...' : `Exportar ${selected.size} cuento${selected.size === 1 ? '' : 's'}`}
        </button>
        {done && (
          <p className="text-xs text-green-700">
            Descargado <code>{done.filename}</code> ({done.stories} cuentos, {done.images} imagenes, {done.audio} audios).
          </p>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </section>
  );
}

function ImportSection() {
  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  function reset() {
    setPreview(null);
    setApplied(null);
    setError(null);
  }

  async function doPreview() {
    if (!file) return;
    setBusy(true);
    reset();
    try {
      const result = await api.admin.importStories(file, { dryRun: true, overwrite });
      setPreview(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function doApply() {
    if (!file) return;
    setBusy(true);
    setApplied(null);
    setError(null);
    try {
      const result = await api.admin.importStories(file, { dryRun: false, overwrite });
      setApplied(result);
      setPreview(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-xl border border-brand-100 bg-white p-4">
      <h3 className="font-semibold text-brand-700">Importar cuentos</h3>
      <p className="mt-1 text-xs text-gray-500">
        Sube un fichero <code>.db</code> generado por la opcion de exportar. Por defecto se hace
        <em> merge</em>: los cuentos ya existentes en este entorno se preservan; solo se anaden los
        que falten. Activa <em>sobrescribir</em> para sustituir los que colisionen por id o slug.
      </p>

      <div className="mt-3 flex flex-col gap-3">
        <input
          type="file"
          accept=".db,application/octet-stream"
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); reset(); }}
          className="text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
          Sobrescribir en colisiones (cuidado: borra el cuento existente)
        </label>
        <div className="flex gap-2">
          <button
            onClick={doPreview}
            disabled={busy || !file}
            className="rounded-lg border border-brand-300 px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            {busy ? 'Analizando...' : 'Vista previa'}
          </button>
          <button
            onClick={doApply}
            disabled={busy || !file}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {busy ? 'Aplicando...' : 'Aplicar import'}
          </button>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {preview && <ImportReport title="Vista previa (sin aplicar)" report={preview} />}
        {applied && <ImportReport title="Import aplicado" report={applied} success />}
      </div>
    </section>
  );
}

function ImportReport({ title, report, success = false }) {
  return (
    <div className={`mt-2 rounded-lg border p-3 text-sm ${success ? 'border-green-200 bg-green-50' : 'border-brand-100 bg-brand-50/50'}`}>
      <h4 className="font-semibold text-gray-800">{title}</h4>
      <ul className="mt-2 space-y-0.5 text-xs text-gray-700">
        <li>Cuentos en el fichero: <strong>{report.snapshotTotal}</strong></li>
        <li>Nuevos a importar: <strong>{report.toInsert}</strong></li>
        <li>Preservados (colision): <strong>{report.preserved}</strong></li>
        {report.overwrite && <li>Sobrescritos: <strong>{report.toReplace}</strong></li>}
        {report.applied && !report.dryRun && (
          <li>Aplicado: cuentos {report.applied.stories} / imagenes {report.applied.images} / audio {report.applied.audio}</li>
        )}
      </ul>
      {report.preservedDetails?.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-gray-600">Ver preservados ({report.preservedDetails.length})</summary>
          <ul className="mt-1 space-y-0.5 pl-3 text-xs text-gray-600">
            {report.preservedDetails.map((s) => (<li key={s.id}><code>{s.slug}</code> (colision por {s.reason})</li>))}
          </ul>
        </details>
      )}
      {report.replacedDetails?.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-gray-600">Ver sobrescritos ({report.replacedDetails.length})</summary>
          <ul className="mt-1 space-y-0.5 pl-3 text-xs text-gray-600">
            {report.replacedDetails.map((s) => (<li key={s.id}><code>{s.slug}</code> (colision por {s.reason})</li>))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ============================================================
// Tab: Log moderacion
// ============================================================
function LogTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.admin.moderationLog(100).then(setData).catch(() => setData({ items: [] })); }, []);
  if (!data) return <p className="text-gray-500">Cargando...</p>;
  if (data.items.length === 0) return <p className="text-gray-500">Sin acciones registradas.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-brand-100 bg-white">
      <table className="w-full text-left text-sm">
        <thead className="bg-brand-50 text-xs uppercase text-brand-700">
          <tr>
            <th className="px-3 py-2">Fecha</th>
            <th className="px-3 py-2">Story ID</th>
            <th className="px-3 py-2">Accion</th>
            <th className="px-3 py-2">Motivo</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((row) => (
            <tr key={row.id} className="border-t border-brand-50">
              <td className="px-3 py-2 text-xs text-gray-500">{new Date(row.created_at).toLocaleString()}</td>
              <td className="px-3 py-2 font-mono text-xs">{row.story_id.slice(0, 8)}...</td>
              <td className="px-3 py-2 text-sm">{row.action}</td>
              <td className="px-3 py-2 text-sm text-gray-600">{row.reason ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
